import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Portal,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { HeroContent } from '@/data/types'
import conferenceImage from '@/assets/dc-conference-2026.jpg'
import patrioticBackground from '@/assets/conference-patriotic-mobile.svg?url'
import EmailSubscribeDialog from '@/components/EmailSubscribeDialog'

// Midnight at the start of November 18 in Washington, DC (Eastern Standard Time).
const conferenceEndsAt = Date.parse('2026-11-18T00:00:00-05:00')

export interface HeroSectionProps {
  /**
   * Content for the hero section (headline, subheadline, image)
   */
  content: HeroContent

  /**
   * Callback when "Join Our Movement" button is clicked
   */
  onJoinClick: () => void

  /**
   * Optional CSS class name for styling customization
   */
  className?: string

  /**
   * Test ID for automated testing
   * @default "hero-section"
   */
  testId?: string
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  content,
  onJoinClick,
  className,
  testId = 'hero-section',
}) => {
  const heroHeadingId = 'hero-heading'
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const ticketSlotRef = useRef<HTMLDivElement>(null)
  const [isTicketInView, setIsTicketInView] = useState(true)
  const floatTickets = isMobile && !isTicketInView

  const [showConference, setShowConference] = useState(
    () => Date.now() < conferenceEndsAt
  )

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const checkExpiry = () => {
      const remaining = conferenceEndsAt - Date.now()
      setShowConference(remaining > 0)
      if (remaining > 0)
        timer = setTimeout(checkExpiry, Math.min(remaining, 86400000))
    }
    checkExpiry()
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!showConference || !isMobile || !ticketSlotRef.current) return

    // Observe the reserved space so fixing the button cannot change visibility.
    const observer = new IntersectionObserver(([entry]) => {
      setIsTicketInView(entry.isIntersecting)
    })
    observer.observe(ticketSlotRef.current)
    return () => observer.disconnect()
  }, [showConference, isMobile])

  const handleJoinClick = () => {
    setIsDialogOpen(true)
    if (onJoinClick) {
      onJoinClick()
    }
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
  }

  return (
    <>
      <EmailSubscribeDialog open={isDialogOpen} onClose={handleDialogClose} />
      {showConference ? (
        <Box
          component="section"
          aria-labelledby={heroHeadingId}
          data-testid={testId}
          className={className}
          sx={{
            position: 'relative',
            isolation: 'isolate',
            overflow: 'hidden',
            bgcolor: 'primary.dark',
            backgroundImage: {
              xs: `url("${patrioticBackground}")`,
              md: 'none',
            },
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            color: 'white',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              display: { xs: 'none', md: 'block' },
              background: {
                md: 'linear-gradient(270deg, rgba(22,20,57,0.78) 0%, rgba(22,20,57,0.74) 35%, rgba(22,20,57,0.7) 48%, rgba(22,20,57,0) 68%)',
              },
              pointerEvents: 'none',
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              pt: { xs: 10, sm: 12, md: 16 },
              pb: { xs: 13, sm: 14, md: 12 },
            }}
          >
            <Box
              sx={{
                maxWidth: { xs: '100%', sm: '36rem', md: '40rem' },
                width: { xs: '100%', md: '44%' },
                ml: 'auto',
              }}
            >
              <Box>
                <Box
                  component="img"
                  src={conferenceImage}
                  alt="Shared parenting advocates gathered on the steps of the U.S. Capitol in Washington, DC"
                  width={1659}
                  height={948}
                  sx={{
                    position: { xs: 'static', md: 'absolute' },
                    inset: 0,
                    zIndex: { xs: 'auto', md: -2 },
                    width: '100%',
                    height: { xs: 'auto', md: '100%' },
                    objectFit: { xs: 'contain', md: 'cover' },
                    objectPosition: { xs: 'center', md: 'center 65%' },
                    display: 'block',
                    borderRadius: { xs: 2, md: 0 },
                    mb: { xs: 3, md: 0 },
                  }}
                />
                <Typography
                  component="p"
                  sx={{ color: '#dbe5f4', fontWeight: 600, mb: 2 }}
                >
                  November 16–18, 2026 • Washington, DC
                </Typography>
                <Typography
                  id={heroHeadingId}
                  component="h1"
                  sx={{
                    color: 'white',
                    fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                    fontWeight: 700,
                    lineHeight: 1.08,
                    letterSpacing: '-0.035em',
                    mb: 3,
                  }}
                >
                  DC Shared Parenting Conference
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    color: '#dbe5f4',
                    fontSize: '1.125rem',
                    lineHeight: 1.65,
                    maxWidth: '44ch',
                    mb: 4,
                  }}
                >
                  Join parents, professionals, and advocates for practical
                  training, policy discussions, and advocacy on Capitol
                  Hill—including a Congressional briefing. Get the tools and
                  connections to advance equal shared parenting.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{
                    '& .MuiButton-root': {
                      minHeight: 52,
                      px: 3,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:focus-visible': {
                        outline: '3px solid white',
                        outlineOffset: 4,
                      },
                      '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                        transform: 'none',
                        '&:hover': { transform: 'none' },
                      },
                    },
                  }}
                >
                  <Box ref={ticketSlotRef} sx={{ minHeight: 52 }}>
                    <Portal disablePortal={!floatTickets}>
                      <Button
                        component="a"
                        href="https://www.zeffy.com/en-US/ticketing/dc-conference-nov-16-18--2026"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Buy Tickets (opens in a new tab)"
                        variant="contained"
                        color="secondary"
                        sx={{
                          minHeight: 52,
                          px: 3,
                          textTransform: 'none',
                          fontSize: '1rem',
                          width: { xs: '100%', sm: 'auto' },
                          ...(floatTickets && {
                            position: 'fixed',
                            left: 16,
                            right: 16,
                            bottom: 'calc(16px + env(safe-area-inset-bottom))',
                            width: 'auto',
                            zIndex: theme.zIndex.appBar + 1,
                          }),
                          '&:focus-visible': {
                            outline: '3px solid white',
                            outlineOffset: 4,
                          },
                          '@media (prefers-reduced-motion: reduce)': {
                            transition: 'none',
                            transform: 'none',
                            '&:hover': { transform: 'none' },
                          },
                        }}
                      >
                        Buy Tickets
                      </Button>
                    </Portal>
                  </Box>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleJoinClick}
                    aria-label="Join our movement - Subscribe to email updates"
                    sx={{
                      color: 'white',
                      borderColor: '#a8b8d0',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    Join our movement
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Container>
        </Box>
      ) : (
        <Box
          component="section"
          role="region"
          aria-labelledby={heroHeadingId}
          data-testid={testId}
          className={className}
          sx={{
            position: 'relative',
            minHeight: { xs: '70vh', sm: '75vh', md: '80vh' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'primary.dark',
            color: 'white',
            overflow: 'hidden',
          }}
        >
          {/* Background Image */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)', // Overlay for text contrast
                zIndex: 1,
              },
            }}
          >
            <picture>
              <source srcSet={content.heroImage.src} type="image/webp" />
              <img
                src={content.heroImage.fallbackSrc}
                alt={content.heroImage.alt}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </picture>
          </Box>

          {/* Content Overlay */}
          <Container
            maxWidth="lg"
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              py: { xs: 8, md: 12 },
            }}
          >
            {/* Headline */}
            <Typography
              id={heroHeadingId}
              variant="h1"
              component="h1"
              sx={{
                fontSize: {
                  xs: '2rem', // 32px mobile
                  sm: '2.75rem', // 44px tablet
                  md: '3.5rem', // 56px desktop
                  lg: '4rem', // 64px large desktop
                },
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 3,
                color: 'white',
                textShadow: '2px 2px 8px rgba(0, 0, 0, 0.8)',
              }}
            >
              {content.headline}
            </Typography>

            {/* Subheadline */}
            <Typography
              variant="h5"
              component="p"
              sx={{
                fontSize: {
                  xs: '1.125rem', // 18px mobile
                  sm: '1.25rem', // 20px tablet
                  md: '1.5rem', // 24px desktop
                },
                fontWeight: 400,
                lineHeight: 1.5,
                mb: 5,
                maxWidth: '800px',
                mx: 'auto',
                color: 'white',
                textShadow: '1px 1px 4px rgba(0, 0, 0, 0.8)',
              }}
            >
              {content.subheadline}
            </Typography>

            {/* Call-to-Action Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleJoinClick}
                aria-label="Join our movement - Subscribe to email updates"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  fontWeight: 600,
                  minWidth: { xs: '100%', sm: '200px' },
                  textTransform: 'none',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease',
                  },
                }}
              >
                Join Our Movement
              </Button>
            </Stack>
          </Container>
        </Box>
      )}
    </>
  )
}
